/* eslint-disable react/jsx-props-no-spreading */
import PropTypes from 'prop-types';
import React, { useContext, useRef, useMemo, useCallback, useState } from 'react';

const ItemRefContext = React.createContext(null);

export const useItemRefContext = () => useContext(ItemRefContext);

export const useItemRefs = () => {
    const { registerRef, unregisterRef, findRef, scrollToRef, selectRef, currentItem } =
        useItemRefContext();
    return { findRef, scrollToRef, registerRef, unregisterRef, selectRef, currentItem };
};

const propTypes = {
    refs: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    children: PropTypes.node.isRequired,
};

const defaultProps = {
    refs: {},
};

export const ItemRefProvider = ({ refs: initialRefs, children }) => {
    const refs = useRef(initialRefs);
    const getKey = useCallback(({ id, type }) => `${type}-${id}`);
    const [currentItem, setCurrentItem] = useState(null);

    const registerRef = useCallback(
        (item, ref) => {
            const key = getKey(item);
            if (!refs.current[key]) {
                refs.current[key] = ref;
            }
        },
        [getKey, refs.current],
    );

    const unregisterRef = useCallback(
        (item) => {
            const key = getKey(item);
            if (refs.current[key]) {
                refs.current = {
                    ...refs.current,
                    [key]: null,
                };
            }
        },
        [refs.current],
    );

    const findRef = useCallback(
        (item) => {
            const key = getKey(item);
            if (refs.current[key]) {
                return refs.current[key];
            }
            return null;
        },
        [refs],
    );

    const scrollToRef = useCallback(
        (item, offset = 0, endOfPage = false) => {
            const ref = findRef(item);
            if (ref !== null && ref.current) {
                const element = ref.current;
                const y = element.getBoundingClientRect().top + window.pageYOffset - offset; // Arbitrary header height
                window.scrollTo({ top: y, behavior: 'smooth' });
                if (element.focus) {
                    element.focus();
                }
            } else if (endOfPage) {
                window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
            }
        },
        [findRef],
    );

    // const focusRef = useCallback(
    //     (item) => {
    //         const ref = findRef(item);
    //         if (ref && ref.current) {
    //             ref.current.blur();
    //         }
    //     },
    //     [findRef],
    // );

    // const blurRef = useCallback(
    //     (item) => {
    //         const ref = findRef(item);
    //         if (ref && ref.current) {
    //             ref.current.blur();
    //         }
    //     },
    //     [findRef],
    // );

    const selectRef = useCallback(
        (item) => {
            const ref = findRef(item);
            if (ref && ref.current) {
                if (currentItem) {
                    // blurRef(currentItem);
                }
                // focusRef(item);
                setCurrentItem(item);
            }
        },
        [currentItem, setCurrentItem, findRef],
    );

    // console.log('all refs', refs);

    const value = useMemo(
        () => ({ scrollToRef, findRef, registerRef, unregisterRef, selectRef, currentItem }),
        [scrollToRef, findRef, registerRef, unregisterRef, selectRef, currentItem],
    );

    return <ItemRefContext.Provider value={value}>{children}</ItemRefContext.Provider>;
};

ItemRefProvider.propTypes = propTypes;
ItemRefProvider.defaultProps = defaultProps;

export default ItemRefContext;
